import { useMutation } from '@apollo/client';
import type {
  NotificationCtpCategory,
  NotificationCtpPreference,
} from '@seek/ca-graphql-schema/types';
import { useMelwaysInfo } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import {
  Divider,
  Drawer,
  Heading,
  IconSettings,
  Spread,
  Stack,
  Text,
  TextLinkButton,
  Toggle,
} from 'braid-design-system';
import React, { useEffect } from 'react';

import { isFeatureActive } from 'src/modules/features';
import {
  updateCTPPreferenceQL,
  getCTPPreferenceQL,
} from 'src/modules/graphql/notifications/preference.graphql';
import { useTypedSelector } from 'src/reducers';
import {
  type UpdateNotificationCtpPreferencesMutation,
  type UpdateNotificationCtpPreferencesMutationVariables,
  type NotificationCtpChannelType,
  type NotificationCtpFrequencyType,
  type NotificationCtpPreferenceProductType,
  NotificationCtpUpdateReasonType,
  type NotificationCtpPreferencesInput,
} from 'src/types/graphql';

import translations from './.vocab';
import PreferenceRow from './preference-row';
import {
  trackCustomiseButtonClick,
  trackTogglePreferencesCategory,
} from './tracking/tealium';

interface CategoryProp {
  category: NotificationCtpCategory;
  preferences: NotificationCtpPreference[];
}

export const ctpPreferencesCategoryInput = (
  categoryPreferences: NotificationCtpPreference[],
  subscribe: boolean,
): NotificationCtpPreferencesInput[] =>
  categoryPreferences?.map((preference) => ({
    id: preference.product.id as NotificationCtpPreferenceProductType,
    notificationsChannel: preference.subscriptions.map(
      ({ channel: { id: channelId }, frequency }) => ({
        channel: channelId as NotificationCtpChannelType,
        frequency: (frequency
          ? frequency.value
          : undefined) as NotificationCtpFrequencyType,
        reason: NotificationCtpUpdateReasonType.PreferenceUi,
        subscribe,
      }),
    ),
  }));

export const buildOptimisticResponse = (
  preferences: NotificationCtpPreference[],
  isCategoryOn: boolean,
): UpdateNotificationCtpPreferencesMutation => ({
  __typename: 'Mutation',
  updateNotificationCtpPreferences: {
    __typename: 'UpdateNotificationCtpPreferencesSuccess',
    success: [
      {
        __typename: 'NotificationCTPPreferenceGroup',
        preferences: preferences.map(
          ({ product, subscriptions, ...restProps }) => ({
            product,
            subscriptions: subscriptions.map(
              ({ subscribed, ...subRestProps }) => ({
                subscribed: isCategoryOn,
                ...subRestProps,
              }),
            ),
            ...restProps,
          }),
        ),
      },
    ],
  },
});

const Category: React.FC<CategoryProp> = ({ category, preferences }) => {
  const { t } = useTranslations(translations);
  const { language } = useMelwaysInfo();
  const country = useTypedSelector(
    (state) => state.page.pageAnalytics.siteCountry,
  ).toUpperCase();
  const zone = useTypedSelector((state) => state.page.pageAnalytics.zone);
  const locale = `${language}-${country}`;
  const onlyProductionOption = !isFeatureActive('show-hidden-preferences');
  const [isDrawerOpen, setIsDrawerOpen] = React.useState<boolean>(false);
  const [isCategoryOn, setIsCategoryOn] = React.useState<boolean>(false);

  useEffect(() => {
    const hasSubscriptionOn = preferences.some((preference) =>
      preference.subscriptions.some((subscription) => subscription.subscribed),
    );
    setIsCategoryOn(Boolean(hasSubscriptionOn));
  }, [preferences]);

  const [updateCTPSubscriptions] = useMutation<
    UpdateNotificationCtpPreferencesMutation,
    UpdateNotificationCtpPreferencesMutationVariables
  >(updateCTPPreferenceQL, {
    refetchQueries: [getCTPPreferenceQL],
  });

  const handleToggleChange = async (value: boolean) => {
    const mutationPreferences = ctpPreferencesCategoryInput(preferences, value);

    const { data } = await updateCTPSubscriptions({
      variables: {
        input: {
          preferences: mutationPreferences,
          zone,
          onlyProductionOption: Boolean(onlyProductionOption),
        },
        locale,
      },
      optimisticResponse: buildOptimisticResponse(preferences, value),
    });

    if (
      data?.updateNotificationCtpPreferences?.__typename ===
      'UpdateNotificationCtpPreferencesSuccess'
    ) {
      trackTogglePreferencesCategory(category.id, value);
    }

    return data;
  };

  const handleCustomiseClick = () => {
    setIsDrawerOpen(true);
    trackCustomiseButtonClick(category.id);
  };

  return (
    <>
      <Stack space="large">
        <Divider />
        <Spread space="medium" alignY="top">
          <Stack space="gutter">
            <Heading
              level="4"
              data={{ testid: `category-${category.id}-title` }}
            >
              {category.label}
            </Heading>
            <Text>{category.description}</Text>
            <Text>
              <TextLinkButton
                data={{ testid: `customise-button-${category.id}` }}
                weight="weak"
                icon={<IconSettings />}
                onClick={handleCustomiseClick}
              >
                {t('text-customise')}
              </TextLinkButton>
            </Text>
          </Stack>
          <Toggle
            id={`category-${category.id}-toggle`}
            label=""
            on={isCategoryOn}
            onChange={handleToggleChange}
          />
        </Spread>
      </Stack>
      <Drawer
        id={`${category.id}-drawer`}
        data={{ testid: `${category.id}-drawer` }}
        title={category.label}
        width="small"
        open={isDrawerOpen}
        closeLabel={t('text-close')}
        onClose={() => {
          setIsDrawerOpen(false);
        }}
      >
        <Stack space="large">
          {preferences.map((preference) => (
            <PreferenceRow
              preference={preference}
              categoryId={category.id}
              key={preference.product.id}
            />
          ))}
        </Stack>
      </Drawer>
    </>
  );
};

export default Category;
